/**
 * Reading a prior pension report, on screen (§10).
 *
 * The card exists because the routes without it were an API a preparer could
 * not reach, which this repo has already learned to call a gap rather than a
 * feature.
 *
 * Two things drive the layout.
 *
 * **The reading is shown with its weaknesses on show.** Confidence, what the
 * reading could not settle, and the concerns it raised are above the figures
 * rather than below them — a preparer deciding whether to trust a reading
 * needs the reasons not to first. The words each figure was lifted from sit
 * beside it, because that is what makes checking take seconds instead of a
 * return trip to the report.
 *
 * **The confirm form is pre-filled but not pre-agreed.** The three headline
 * figures arrive from the reading and are editable, and the form will not
 * submit without a note saying what was checked. Typing a figure you have
 * just verified against the page is the moment somebody takes responsibility
 * for it; inheriting it silently is not.
 */

import { useEffect, useState } from 'react';
import {
  api,
  type EngagementDocument,
  type PensionPosition,
  type PensionReadItem,
  type PensionReading,
} from '../lib/api';
import { Badge, Card, EmptyState, SectionHeading } from './ui';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const money = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : v;
};

const find = (items: PensionReadItem[], key: string) => items.find((i) => i.key === key)?.value ?? '';

/** A rate reads as a percentage; a mortality basis reads as itself. */
function assumptionValue(item: PensionReadItem): string {
  if (item.unit !== 'ratio') return item.value;
  const n = Number(item.value);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : item.value;
}

export function PensionReports({
  engagementId,
  canWrite,
  onChange,
}: {
  engagementId: string;
  canWrite: boolean;
  onChange?: () => void | Promise<void>;
}) {
  const [readings, setReadings] = useState<PensionReading[] | null>(null);
  const [positions, setPositions] = useState<PensionPosition[]>([]);
  // Loaded here rather than threaded through the workspace: no other screen
  // lists documents yet, and a card that offers "read this report" needs to
  // know what has been uploaded.
  const [documents, setDocuments] = useState<EngagementDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  async function load() {
    try {
      const [r, d] = await Promise.all([
        api.pensionReadings(engagementId),
        api.documents(engagementId),
      ]);
      setReadings(r.readings);
      setPositions(r.positions);
      setDocuments(d.documents);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  async function requestRead(documentId: string) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.readPensionReport(engagementId, documentId);
      setNotice(res.note);
      await load();
      await onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(readingId: string) {
    const note = window.prompt('Why is this reading being dismissed?');
    if (!note) return;
    try {
      await api.dismissPensionReading(engagementId, readingId, note);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (!readings) return null;

  const held = readings.filter((r) => r.status === 'proposed');
  const resolved = readings.filter((r) => r.status !== 'proposed');
  // A reading already answered should not be offered again, and neither
  // should one in flight.
  const readDocumentIds = new Set(readings.map((r) => r.documentId));
  const candidates = documents.filter((d) => !readDocumentIds.has(d.id));

  return (
    <Card>
      <SectionHeading
        title="Pension reports"
        subtitle="A prior actuarial report or IAS 19 disclosure, read into the position it discloses. The engine carries the scheme actuary's figures forward — it does not produce its own, and it holds no mortality tables."
      />

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {notice}
        </div>
      ) : null}

      {positions.length > 0 ? (
        <div className="mb-6">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Recorded positions
          </h4>
          <div className="space-y-2">
            {positions.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{p.schemeName}</span>
                  <span className="text-xs text-slate-500">
                    {p.basis} · at {p.measurementDate}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-slate-500">Obligation</dt>
                    <dd className="tabular-nums">
                      {p.currency} {money(p.definedBenefitObligation)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Assets</dt>
                    <dd className="tabular-nums">
                      {p.currency} {money(p.fairValueOfAssets)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Net</dt>
                    <dd className="tabular-nums">
                      {p.currency} {money(p.netPosition)}
                    </dd>
                  </div>
                </dl>
                <p className="mt-2 text-xs text-slate-500">
                  <Badge tone="violet">{p.provenance.replace(/_/g, ' ')}</Badge>{' '}
                  {p.actuaryName ? `${p.actuaryName} · ` : ''}
                  checked: {p.checked}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {held.map((r) =>
        confirming === r.id ? (
          <ConfirmForm
            key={r.id}
            engagementId={engagementId}
            reading={r}
            onCancel={() => setConfirming(null)}
            onDone={async () => {
              setConfirming(null);
              await load();
              await onChange?.();
            }}
          />
        ) : (
          <HeldReading
            key={r.id}
            reading={r}
            canWrite={canWrite}
            onConfirm={() => setConfirming(r.id)}
            onDismiss={() => void dismiss(r.id)}
          />
        ),
      )}

      {held.length === 0 && positions.length === 0 ? (
        <EmptyState
          title="No pension report has been read"
          detail="Upload an actuarial report or a set of accounts carrying the pension note, then read it below."
        />
      ) : null}

      {canWrite && candidates.length > 0 ? (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Read a report
          </h4>
          <div className="flex flex-wrap gap-2">
            {candidates.map((d) => (
              <button
                key={d.id}
                type="button"
                disabled={busy}
                onClick={() => void requestRead(d.id)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:border-teal-400 disabled:opacity-50"
              >
                {d.filename}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Reading is queued: an actuarial valuation is a long call against a large attachment. The
            reading appears here for confirmation when it completes.
          </p>
        </div>
      ) : null}

      {resolved.length > 0 ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-slate-500">
            {resolved.length} answered reading(s)
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-slate-500">
            {resolved.map((r) => (
              <li key={r.id}>
                {r.schemeName ?? 'Unnamed scheme'} — {r.status}
                {r.resolutionNote ? `: ${r.resolutionNote}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}

function HeldReading({
  reading,
  canWrite,
  onConfirm,
  onDismiss,
}: {
  reading: PensionReading;
  canWrite: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">
          {reading.schemeName ?? 'Scheme not named in the report'}
        </span>
        <span className="text-xs text-slate-500">
          {reading.basis ?? 'basis not stated'} ·{' '}
          {reading.measurementDate ?? 'no measurement date'}
        </span>
      </div>

      {/* The reasons not to trust it, before the figures. */}
      {reading.notes ? (
        <p className="mt-2 whitespace-pre-line rounded-lg bg-amber-100/60 px-3 py-2 text-xs text-amber-900">
          {reading.notes}
        </p>
      ) : null}
      {reading.uncertain.length > 0 ? (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-900">
          {reading.uncertain.map((u) => (
            <li key={u.key}>
              <span className="font-medium">{u.key}</span>: {u.reason}
            </li>
          ))}
        </ul>
      ) : null}

      <ItemTable title="Figures" items={reading.figures} />
      <ItemTable title="Assumptions" items={reading.assumptions} assumption />

      {canWrite ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white"
          >
            Confirm the position
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ItemTable({
  title,
  items,
  assumption = false,
}: {
  title: string;
  items: PensionReadItem[];
  assumption?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h5>
      <table className="mt-1 w-full text-sm">
        <tbody>
          {items.map((i) => (
            <tr key={i.key} className="border-b border-slate-100 align-top">
              <td className="py-1.5 pr-3">{i.label}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {assumption ? assumptionValue(i) : money(i.value)}
              </td>
              <td className="py-1.5 pr-3 text-xs text-slate-500">
                {i.source_text ? `“${i.source_text}”` : ''}
                {i.page ? ` p.${i.page}` : ''}
              </td>
              <td className="py-1.5 text-right text-xs text-slate-400">
                {(i.confidence * 100).toFixed(0)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfirmForm({
  engagementId,
  reading,
  onCancel,
  onDone,
}: {
  engagementId: string;
  reading: PensionReading;
  onCancel: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    schemeName: reading.schemeName ?? '',
    measurementDate: reading.measurementDate ?? '',
    basis: reading.basis ?? '',
    currency: reading.currency ?? 'GBP',
    definedBenefitObligation: find(reading.figures, 'defined_benefit_obligation'),
    fairValueOfAssets: find(reading.figures, 'fair_value_of_assets'),
    netPosition: find(reading.figures, 'net_position'),
    actuaryName: '',
    reportReference: '',
    checked: '',
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.confirmPensionReading(engagementId, reading.id, {
        ...form,
        actuaryName: form.actuaryName || undefined,
        reportReference: form.reportReference || undefined,
        // The assumptions travel as read, mortality basis included, as text.
        assumptions: reading.assumptions.map((a) => ({
          key: a.key,
          label: a.label,
          value: a.value,
          unit: a.unit ?? 'text',
        })),
      });
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const field = (k: keyof typeof form, label: string, extra: Record<string, unknown> = {}) => (
    <label className="text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <input
        className={INPUT}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        {...extra}
      />
    </label>
  );

  return (
    <form onSubmit={(e) => void submit(e)} className="mb-4 rounded-xl border border-teal-200 bg-teal-50/40 px-4 py-4">
      <p className="mb-3 text-sm text-slate-700">
        Recording the scheme actuary's position as <strong>external expert</strong> evidence. The
        figures below are the reading's — check each against the report and correct any that are
        wrong before confirming.
      </p>

      {error ? (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        {field('schemeName', 'Scheme', { required: true })}
        {field('measurementDate', 'Measurement date', {
          required: true,
          type: 'date',
          placeholder: 'YYYY-MM-DD',
        })}
        {field('basis', 'Basis', { required: true, placeholder: 'IAS 19' })}
        {field('currency', 'Currency', { required: true, maxLength: 3 })}
        {field('definedBenefitObligation', 'Defined benefit obligation', { required: true })}
        {field('fairValueOfAssets', 'Fair value of assets', { required: true })}
        {field('netPosition', 'Net position', { required: true })}
        {field('actuaryName', 'Actuary', { placeholder: 'Who produced the report' })}
        {field('reportReference', 'Report reference')}
      </div>

      <label className="mt-3 block text-sm">
        <span className="font-medium text-slate-700">What did you check?</span>
        <textarea
          className={INPUT}
          rows={3}
          required
          value={form.checked}
          onChange={(e) => setForm({ ...form, checked: e.target.value })}
          placeholder="e.g. Agreed the obligation, assets and net position to the pension note on page 34; measurement date agreed to the heading; basis stated as IAS 19."
        />
        <span className="mt-1 block text-xs text-slate-500">
          Required. This is the largest single figure in many sponsors' accounts, and a reliance on
          an expert that records no checking is not a reliance.
        </span>
      </label>

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
        >
          {saving ? 'Recording…' : 'Record the position'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
